import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCredits } from '../hooks/useCredits';
import { usePricing } from '../contexts/PricingContext';
import { BuyButton } from '../components/shared/BuyButton';
import { useCurrency } from '../contexts/CurrencyContext';

interface CreditPackGroup {
  type: 'questions' | 'synastry' | 'astromatch';
  label: string;
  emoji: string;
  packages: { id: string; label: string }[];
}

const CREDIT_GROUPS: CreditPackGroup[] = [
  {
    type: 'questions', label: 'Perguntas aos Astros', emoji: '⭐',
    packages: [
      { id: 'questions_q3',  label: '3 Perguntas' },
      { id: 'questions_q5',  label: '5 Perguntas' },
      { id: 'questions_q10', label: '10 Perguntas' },
    ],
  },
  {
    type: 'astromatch', label: 'AstroMatch', emoji: '🔮',
    packages: [
      { id: 'astromatch_a3',  label: '3 AstroMatch' },
      { id: 'astromatch_a10', label: '10 AstroMatch' },
    ],
  },
  {
    type: 'synastry', label: 'Sinastria', emoji: '💫',
    packages: [
      { id: 'synastry_s1', label: '1 Sinastria' },
      { id: 'synastry_s3', label: '3 Sinastrias' },
    ],
  },
];

function BalanceCard({ emoji, label, balance }: { emoji: string; label: string; balance: number }) {
  return (
    <div className="card" style={{ flex: '1 1 120px', textAlign: 'center', backgroundColor: balance > 0 ? 'rgba(31,78,95,0.04)' : 'var(--bg-surface)', border: balance > 0 ? '1px solid var(--border-gold)' : '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: '1.75rem', display: 'block', marginBottom: '0.5rem' }}>{emoji}</span>
      <p style={{ fontSize: 'clamp(1.5rem,4vw,2rem)', fontWeight: 700, color: balance > 0 ? 'var(--petrol)' : '#fca5a5', fontFamily: 'var(--font-display)', margin: '0 0 0.25rem' }}>
        {balance}
      </p>
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{label}</p>
    </div>
  );
}

function CreditPackCard({ pkg }: { pkg: { id: string; label: string }; }) {
  const { getProduct } = usePricing();
  const { format } = useCurrency();
  const product = getProduct(pkg.id);
  if (!product) return null;

  const price   = product.price_brl;
  const credits = product.credits ?? 1;
  const perUnit = price / credits;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.2rem', fontSize: 'var(--text-label)' }}>
            {product.name}
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
            {format(perUnit)}/unidade
          </p>
        </div>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', color: 'var(--petrol)', fontWeight: 700 }}>
          {format(price)}
        </span>
      </div>
      <BuyButton productId={pkg.id} className="btn btn-primary btn-sm btn-block" label={`✦ Comprar ${product.name}`} />
    </div>
  );
}

export function CreditWalletPage() {
  const navigate         = useNavigate();
  const { credits }      = useCredits();
  const [activeGroup, setActiveGroup] = useState<string>('questions');

  const currentGroup = CREDIT_GROUPS.find(g => g.type === activeGroup) ?? CREDIT_GROUPS[0];

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Carteira
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Seus créditos e compras
        </p>
      </div>

      {/* Subscription CTA */}
      <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid var(--border-gold)', backgroundColor: 'rgba(31,78,95,0.04)', textAlign: 'center' }}>
        <span style={{ fontSize: '1.75rem', display: 'block', marginBottom: '0.5rem' }}>✦</span>
        <p className="font-display" style={{ color: 'var(--text-primary)', fontSize: '1rem', marginBottom: '0.25rem' }}>
          Assine e acesse tudo ilimitado
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
          Mapa astral, previsões, tarot e muito mais
        </p>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
          Ver planos ✦
        </button>
      </div>

      {/* Balance overview */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {CREDIT_GROUPS.map(g => (
          <BalanceCard key={g.type} emoji={g.emoji} label={g.label} balance={credits[g.type as keyof typeof credits] ?? 0} />
        ))}
      </div>

      {/* Buy more */}
      <div>
        <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>
          Comprar créditos
        </h2>

        {/* Tab selector */}
        <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem', overflowX: 'auto' }}>
          {CREDIT_GROUPS.map(g => (
            <button key={g.type} onClick={() => setActiveGroup(g.type)}
              className={activeGroup === g.type ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {g.emoji} {g.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {currentGroup.packages.map(pkg => (
            <CreditPackCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      </div>

      {/* Low credit banners */}
      {credits.questions === 1 && (
        <div style={{ marginTop: '1.5rem', padding: '0.75rem 1rem', backgroundColor: 'rgba(31,78,95,0.08)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-md)' }}>
          <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)', margin: 0 }}>
            ⭐ Último crédito de pergunta disponível, <button className="btn btn-ghost btn-sm" onClick={() => setActiveGroup('questions')} style={{ padding: '0 0.25rem', display: 'inline' }}>Reabasteça →</button>
          </p>
        </div>
      )}
    </div>
  );
}
